import React, { useCallback, useEffect, useMemo, useState } from "react";
import { tableUseStyle } from "../../etc/tableusestyle";
import { DataGridPremium, GridColDef, GridToolbar, GridRowSelectionModel, GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-premium";
import { Button } from "@mui/joy";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../fetch/fetch";
import CustomInput from "../../etc/custominput";
import WinterManualAddModal from "./wintermanualaddmodal";
import WinterSendLinkModal from "./wintersendlinkmodal";
import WinterSendHistoryModal from "./wintersendhistorymodal";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

/**
 * 본사직원페이지 > '윈터 스쿨 등록 관리' — suneungsunbae.com/winter 사전 예약 신청 리스트.
 * 서버: /dashboard/winterSchoolPrereserve/list · /memo · /hide · /manual (users.todoistApiKey 보유자 전용).
 * - '수동 추가하기'로 넣은 행(isManual = 1)은 목록·집계표에는 들어가지만 일별 추이 그래프에서는 뺀다.
 * - 목록은 서버가 숨기지 않은(isHide = 0) 행만 내려준다. 집계표도 그 행 기준.
 * - 메모 셀은 더블클릭 편집 → processRowUpdate 에서 서버 저장, 실패 시 이전 값으로 롤백.
 * - 맨 앞 체크박스로 여러 명 선택 → '숨기기 (n명)' = 서버 isHide = 1(되돌릴 수 없음, 해제는 MySQL 에서 직접) / '결제링크 알림톡 발송 (n명)' = wintersendlinkmodal.
 * - '결제링크 발송시각' 열 = 발송 성공 횟수(서버 sendCount). 숫자를 누르면 wintersendhistorymodal 로 건별 시각·결과.
 */

// 랜딩 폼(suneungclient3 data/winter.ts WINTER_BRANCHES)·서버 BRANCHES 와 같은 순서. DB location 값 그대로('점' 없음)
const WINTER_BRANCHES = ["대치", "강남", "송파", "분당", "수성", "김포", "신촌", "강동", "하남", "센텀", "동탄"];

// 차트 테마 — charts/InflowPage.tsx 와 동일
const CARD_BG = "#FFFFFF";
const BORDER = "#E5E7EB";
const TEXT_MAIN = "#111827";
const TEXT_SUB = "#6B7280";
const LINE_COLOR = "#185FA5";

const toDateKey = (v: any) => {
    const d = new Date(v);
    if (isNaN(d.getTime())) return "";
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const toastOptions: any = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    theme: "colored",
};

const formatDate = (v: any) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const formatPhone = (v: any) => {
    const d = String(v || "").replace(/[^0-9]/g, "");
    if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    return String(v || "");
};

const WinterRegister: React.FC<any> = (props) => {

    const classes = tableUseStyle();

    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [branch, setBranch] = useState<string>("전체");
    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
    const [hiding, setHiding] = useState<boolean>(false);
    const [sendOpen, setSendOpen] = useState<boolean>(false);
    const [historyTarget, setHistoryTarget] = useState<any | null>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [chartBranch, setChartBranch] = useState<string>("전체");
    const [manualOpen, setManualOpen] = useState<boolean>(false);

    const fetchList = useCallback(async () => {
        try {
            setLoading(true);
            setSelectionModel([]);
            const result: any = await getFetch("/dashboard/winterSchoolPrereserve/list");
            if (result.message === "success" && Array.isArray(result.data)) {
                setRows(result.data);
            } else {
                console.log(result);
                toast.error("목록을 불러오지 못했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("목록을 불러오지 못했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    // 필터 탭 + 검색어에 따른 표시 행 — 집계표는 필터와 무관하게 rows 전체 기준
    // 검색은 allstudents.tsx nameChange 와 같은 규칙: 첫 글자가 숫자면 전화번호, 아니면 이름 contains
    const visibleRows = useMemo(() => {
        const text = searchText.trim();
        const isNumber = text.length > 0 && !isNaN(+text[0]);
        return rows.filter((row: any) => {
            if (branch !== "전체" && row.location !== branch) return false;
            if (!text) return true;
            if (isNumber) return String(row.phoneNumber || "").replace(/[^0-9]/g, "").includes(text.replace(/[^0-9]/g, ""));
            return String(row.name || "").includes(text);
        });
    }, [rows, branch, searchText]);

    // 지점별 신청자 수 (서버가 이미 숨긴 행을 뺀 상태)
    const branchCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        WINTER_BRANCHES.forEach((b) => { counts[b] = 0; });
        rows.forEach((row: any) => {
            if (counts[row.location] === undefined) counts[row.location] = 0;
            counts[row.location] += 1;
        });
        return counts;
    }, [rows]);

    // 랜딩 폼 지점 목록에 없는 location 이 DB에 있으면 집계표 맨 아래에 같이 보여준다
    const summaryBranches = useMemo(() => {
        const extra = Object.keys(branchCounts).filter((b) => !WINTER_BRANCHES.includes(b));
        return [...WINTER_BRANCHES, ...extra];
    }, [branchCounts]);

    // 일별 신청자 수 — 첫 신청일부터 오늘까지 빈 날은 0으로 채운다. 지점 칩은 표 필터와 별개. 수동 추가(isManual)는 제외
    const chartData = useMemo(() => {
        const source = rows.filter((row: any) => !row.isManual);
        const target = chartBranch === "전체" ? source : source.filter((row: any) => row.location === chartBranch);
        if (source.length === 0) return [];
        const counts: Record<string, number> = {};
        target.forEach((row: any) => {
            const key = toDateKey(row.createdAt);
            if (!key) return;
            counts[key] = (counts[key] || 0) + 1;
        });
        // 기간은 지점과 무관하게 전체 첫 신청일 ~ 오늘 (지점 바꿔도 x축 고정)
        let minTime = Infinity;
        source.forEach((row: any) => {
            const t = new Date(row.createdAt).getTime();
            if (!isNaN(t) && t < minTime) minTime = t;
        });
        if (minTime === Infinity) return [];
        const cursor = new Date(minTime);
        cursor.setHours(0, 0, 0, 0);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const result: { date: string, count: number }[] = [];
        while (cursor.getTime() <= today.getTime()) {
            const key = toDateKey(cursor);
            result.push({ date: key, count: counts[key] || 0 });
            cursor.setDate(cursor.getDate() + 1);
        }
        return result;
    }, [rows, chartBranch]);

    const columns: GridColDef[] = [
        { field: "location", headerName: "지점", width: 90 },
        { field: "name", headerName: "이름", width: 110 },
        {
            field: "phoneNumber",
            headerName: "전화번호",
            width: 140,
            valueFormatter: (params: any) => formatPhone(params.value),
        },
        {
            field: "createdAt",
            headerName: "신청일시",
            width: 150,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "sendCount",
            headerName: "결제링크 발송시각",
            width: 140,
            type: "number",
            align: "center",
            headerAlign: "center",
            renderCell: (params: any) => {
                const count = +(params.value || 0);
                return (
                    <Button
                        size="sm"
                        variant={count > 0 ? "soft" : "plain"}
                        color={count > 0 ? "primary" : "neutral"}
                        disabled={count === 0}
                        onClick={(e) => { e.stopPropagation(); setHistoryTarget(params.row); }}
                        sx={{ minHeight: "24px", fontSize: "13px", padding: "0 12px" }}
                    >
                        {count}
                    </Button>
                );
            },
        },
        { field: "memo", headerName: "메모", width: 260, flex: 1, editable: true },
    ];

    // 체크한 행 (표시 중인 행 기준 — 필터로 안 보이는 행은 선택에서 제외)
    const selectedRows = useMemo(() => {
        const ids = new Set(selectionModel.map((id) => String(id)));
        return visibleRows.filter((row: any) => ids.has(String(row.id)));
    }, [selectionModel, visibleRows]);

    // 메모 편집 저장 — 실패하면 이전 행으로 되돌린다
    const handleRowUpdate = async (newRow: any, oldRow: any) => {
        try {
            if ((newRow.memo || "") !== (oldRow.memo || "")) {
                const r: any = await postFetch("/dashboard/winterSchoolPrereserve/memo", { id: newRow.id, memo: newRow.memo || "" });
                if (r.message !== "success") throw new Error(r.message);
            }
            return newRow;
        } catch (e) {
            console.log(e);
            toast.error("메모 저장에 실패했습니다. 다시 시도해 주세요.", toastOptions);
            return oldRow;
        }
    };

    // 체크한 행 숨기기(여러 명) — 성공 시 목록에서 제거(재조회 없음)
    const handleHide = async () => {
        if (selectedRows.length === 0 || hiding) return;
        if (!window.confirm(`${selectedRows.length}명을 숨김 처리할까요? 화면에서는 되돌릴 수 없습니다.`)) return;
        const targets = selectedRows;
        try {
            setHiding(true);
            const r: any = await postFetch("/dashboard/winterSchoolPrereserve/hide", { ids: targets.map((t: any) => t.id) });
            if (r.message !== "success") throw new Error(r.message);
            const hidden = new Set(targets.map((t: any) => t.id));
            setRows((prev) => prev.filter((row: any) => !hidden.has(row.id)));
            setSelectionModel([]);
            toast.success(`${targets.length}명을 숨김 처리했습니다`, toastOptions);
        } catch (e) {
            console.log(e);
            toast.error("숨기기에 실패했습니다. 다시 시도해 주세요.", toastOptions);
        } finally {
            setHiding(false);
        }
    };

    const cellStyle: React.CSSProperties = {
        padding: "8px 16px",
        borderBottom: "1px solid #e5e8eb",
        fontFamily: "Apple_R",
        fontSize: "14px",
        color: "#191f28",
    };

    return (
        <div style={{ width: "96%" }}>
            <div style={{
                fontFamily: "Apple_B",
                marginTop: "50px",
                fontSize: "18px",
                display: "flex",
                alignItems: "center",
            }}>
                <div>
                    윈터 스쿨 등록 관리
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", marginTop: "24px", flexWrap: "wrap", gap: "12px" }}>
                <ToggleButtonGroup
                    color="primary"
                    size="small"
                    exclusive
                    value={branch}
                    onChange={(e, value) => {
                        if (!value) return;
                        setBranch(value);
                        setSelectionModel([]);
                    }}
                >
                    <ToggleButton value="전체">전체</ToggleButton>
                    {WINTER_BRANCHES.map((b) => (
                        <ToggleButton value={b} key={b}>{b}</ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "16px" }}>
                    <CustomInput
                        value={searchText}
                        onChange={(e: any) => { setSearchText(e.target.value); setSelectionModel([]); }}
                        placeholder="이름 or 전화번호"
                    />
                    <Button size="sm" variant="solid" onClick={() => setManualOpen(true)}>
                        수동 추가하기
                    </Button>
                    <Button
                        size="sm"
                        variant="solid"
                        color="primary"
                        disabled={selectedRows.length === 0}
                        onClick={() => setSendOpen(true)}
                    >
                        {selectedRows.length > 0 ? `결제링크 알림톡 발송 (${selectedRows.length}명)` : "결제링크 알림톡 발송"}
                    </Button>
                    <Button
                        size="sm"
                        variant="outlined"
                        color="danger"
                        disabled={selectedRows.length === 0}
                        loading={hiding}
                        onClick={handleHide}
                    >
                        {selectedRows.length > 0 ? `숨기기 (${selectedRows.length}명)` : "숨기기"}
                    </Button>
                </div>
            </div>

            <div className={classes.root} style={{ height: "560px", width: "100%", marginTop: "16px", backgroundColor: "white" }}>
                <DataGridPremium
                    rows={visibleRows}
                    columns={columns}
                    density="compact"
                    loading={loading}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectionModel}
                    onRowSelectionModelChange={(m) => setSelectionModel(m)}
                    initialState={{ pinnedColumns: { left: [GRID_CHECKBOX_SELECTION_COL_DEF.field] } }}
                    processRowUpdate={handleRowUpdate}
                    onProcessRowUpdateError={(e) => console.log(e)}
                    slots={{ toolbar: GridToolbar }}
                />
            </div>

            <div style={{ marginTop: "28px", marginBottom: "60px", display: "flex", alignItems: "flex-start", gap: "24px" }}>
                {/* 일별 신청자 추이 — charts/InflowPage.tsx '기간 내 일별 추이' 와 같은 구성 */}
                <div style={{ flex: 1, minWidth: 0, background: CARD_BG, borderRadius: 12, padding: "20px 24px", border: `1px solid ${BORDER}`, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: TEXT_MAIN, marginBottom: 4, fontFamily: "Apple_B" }}>일별 신청자 추이</div>
                    <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 12 }}>지점 칩을 클릭하면 해당 지점만 표시</div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingBottom: 14, borderBottom: `1px dashed ${BORDER}`, marginBottom: 12 }}>
                        <span style={{ fontSize: 12, color: TEXT_SUB, marginRight: 4 }}>지점</span>
                        {["전체", ...WINTER_BRANCHES].map((b) => {
                            const active = chartBranch === b;
                            return (
                                <button
                                    key={b}
                                    onClick={() => setChartBranch(b)}
                                    style={{
                                        padding: "4px 14px", borderRadius: 20,
                                        border: `1px solid ${active ? "#4F46E5" : BORDER}`,
                                        background: active ? "#4F46E5" : CARD_BG,
                                        color: active ? "#fff" : TEXT_SUB,
                                        cursor: "pointer", fontSize: 12, fontWeight: active ? 600 : 400,
                                    }}
                                >
                                    {b}
                                </button>
                            );
                        })}
                    </div>

                    <div style={{ fontSize: 12, color: TEXT_SUB, marginBottom: 10 }}>
                        현재: {chartBranch === "전체" ? "전체 합계" : chartBranch} · 첫 신청일부터 오늘까지 · 수동 추가분 제외
                    </div>

                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart key={chartBranch} data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={BORDER} />
                            <XAxis
                                dataKey="date"
                                tick={{ fill: TEXT_SUB, fontSize: 10 }}
                                tickFormatter={(v: string) => {
                                    if (!v) return "";
                                    const parts = v.split("-");
                                    if (parts.length === 3) return `${+parts[1]}.${+parts[2]}`;
                                    return v;
                                }}
                            />
                            <YAxis allowDecimals={false} tick={{ fill: TEXT_SUB, fontSize: 11 }} />
                            <Tooltip
                                contentStyle={{ background: CARD_BG, border: `1px solid ${BORDER}`, color: TEXT_MAIN }}
                                formatter={(value: any) => [`${value}명`, "신청자"]}
                            />
                            <Line type="monotone" dataKey="count" stroke={LINE_COLOR} strokeWidth={2} dot={false} name="신청자" isAnimationActive={true} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
                <div style={{ fontFamily: "Apple_B", fontSize: "16px", marginBottom: "10px", width: "280px" }}>
                    신청자 집계
                </div>
                <table style={{ borderCollapse: "collapse", width: "280px", border: "1px solid #e5e8eb" }}>
                    <tbody>
                        <tr style={{ backgroundColor: "#f5f6f8" }}>
                            <td style={{ ...cellStyle, fontFamily: "Apple_B" }}>총 신청자 수</td>
                            <td style={{ ...cellStyle, fontFamily: "Apple_B", textAlign: "right" }}>{rows.length}명</td>
                        </tr>
                        {summaryBranches.map((b, index) => {
                            const count = branchCounts[b] || 0;
                            const isLast = index === summaryBranches.length - 1;
                            const rowCellStyle = isLast ? { ...cellStyle, borderBottom: "none" } : cellStyle;
                            return (
                                <tr key={b}>
                                    <td style={rowCellStyle}>{b}</td>
                                    <td style={{ ...rowCellStyle, textAlign: "right" }}>{count}명</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            </div>

            <WinterManualAddModal
                open={manualOpen}
                onClose={() => setManualOpen(false)}
                branches={WINTER_BRANCHES}
                onSuccess={(row: any) => {
                    // 재조회 없이 맨 위에 추가 (목록은 createdAt DESC)
                    setRows((prev) => [{ ...row, sendCount: 0 }, ...prev]);
                    setSelectionModel([]);
                }}
            />

            <WinterSendLinkModal
                open={sendOpen}
                onClose={() => setSendOpen(false)}
                targets={selectedRows}
                onDone={() => fetchList()}
            />

            <WinterSendHistoryModal
                open={!!historyTarget}
                onClose={() => setHistoryTarget(null)}
                target={historyTarget}
            />
        </div>
    );
};

export default WinterRegister;
